/**
 * AI Service Client for Audra Flow
 * 
 * Provides typed access to the AI service endpoints:
 * - Chat/RAG: Query project knowledge base
 * - Agents: Generate content (goals, personas, etc.)
 * - Analysis: Product Guru recommendations
 * - Documents: Upload and process documents
 *
 * In production, all AI requests go through the backend proxy (/api/ai/*).
 * The backend forwards them to the internal AI service (not exposed publicly).
 */

// AI Service URL:
// - In production: use same-origin proxy (/api/ai) → backend → internal AI service
// - In development: can use VITE_AI_SERVICE_URL to point at local AI service, or use proxy
const AI_SERVICE_URL = import.meta.env.VITE_AI_SERVICE_URL || "/api/ai"

// ==================== Types ====================

export interface Citation {
  documentId: string
  documentName: string
  chunkId: string
  content: string
  page?: number
  score: number
}

export interface ChatRequest {
  projectId: string
  message: string
  conversationId?: string
}

export interface ChatResponse {
  response: string
  citations: Citation[]
  conversationId: string
}

export interface GenerateRequest {
  projectId: string
  artifactType: "goal" | "persona" | "journey" | "usecase" | "storyboard" | "competitor"
  context?: Record<string, unknown>
}

export interface GenerateResponse {
  content: Record<string, unknown>
  confidence: number
  suggestions?: string[]
}

export interface AnalysisRequest {
  projectId: string
}

export interface Recommendation {
  id: string
  type: "gap" | "improvement" | "risk" | "opportunity"
  title: string
  description: string
  impact: "low" | "medium" | "high" | "critical"
  effort: "low" | "medium" | "high"
  category: string
  artifacts?: string[]
  suggestedActions?: string[]
}

export interface AnalysisResponse {
  recommendations: Recommendation[]
  summary: string
  coverage: {
    discovery: number
    definition: number
    design: number
    delivery: number
  }
}

export interface DocumentUploadRequest {
  projectId: string
  file: File
  category?: string
}

export interface DocumentUploadResponse {
  id: string
  name: string
  status: "pending" | "processing" | "processed" | "failed"
  chunksCount?: number
  error?: string
}

export interface DocumentStatus {
  id: string
  name: string
  status: "pending" | "processing" | "processed" | "failed"
  chunksCount?: number
  processedAt?: string
  error?: string
}

// ==================== Error Handling ====================

export class AIServiceError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public details?: unknown
  ) {
    super(message)
    this.name = "AIServiceError"
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: response.statusText }))
    throw new AIServiceError(
      errorData.detail || "AI service request failed",
      response.status,
      errorData
    )
  }
  return response.json()
}

// ==================== API Client ====================

export const aiClient = {
  /**
   * Chat with project knowledge base using RAG
   */
  async chat(request: ChatRequest, token?: string): Promise<ChatResponse> {
    const response = await fetch(`${AI_SERVICE_URL}/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        project_id: request.projectId,
        message: request.message,
        conversation_id: request.conversationId,
      }),
    })

    const data = await handleResponse<{
      response: string
      citations: Array<{
        document_id: string
        document_name: string
        chunk_id: string
        content: string
        page?: number
        score: number
      }>
      conversation_id: string
    }>(response)

    return {
      response: data.response,
      citations: data.citations.map((c) => ({
        documentId: c.document_id,
        documentName: c.document_name,
        chunkId: c.chunk_id,
        content: c.content,
        page: c.page,
        score: c.score,
      })),
      conversationId: data.conversation_id,
    }
  },

  /**
   * Generate content using AI agents
   */
  async generate(request: GenerateRequest, token?: string): Promise<GenerateResponse> {
    const response = await fetch(`${AI_SERVICE_URL}/agents/generate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        project_id: request.projectId,
        artifact_type: request.artifactType,
        context: request.context,
      }),
    })

    return handleResponse<GenerateResponse>(response)
  },

  /**
   * Get Product Guru analysis and recommendations
   */
  async analyze(projectId: string, token?: string): Promise<AnalysisResponse> {
    const response = await fetch(`${AI_SERVICE_URL}/analysis/project/${projectId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    })

    return handleResponse<AnalysisResponse>(response)
  },

  /**
   * Upload document for processing
   */
  async uploadDocument(request: DocumentUploadRequest, token?: string): Promise<DocumentUploadResponse> {
    const formData = new FormData()
    formData.append("file", request.file)
    formData.append("project_id", request.projectId)
    if (request.category) {
      formData.append("category", request.category)
    }

    const response = await fetch(`${AI_SERVICE_URL}/documents/upload`, {
      method: "POST",
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    })

    return handleResponse<DocumentUploadResponse>(response)
  },

  /**
   * Get document processing status
   */
  async getDocumentStatus(documentId: string, token?: string): Promise<DocumentStatus> {
    const response = await fetch(`${AI_SERVICE_URL}/documents/${documentId}/status`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    })

    return handleResponse<DocumentStatus>(response)
  },

  /**
   * List project documents
   */
  async listDocuments(projectId: string, token?: string): Promise<DocumentStatus[]> {
    const response = await fetch(`${AI_SERVICE_URL}/documents?project_id=${projectId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    })

    return handleResponse<DocumentStatus[]>(response)
  },

  /**
   * Delete a document
   */
  async deleteDocument(documentId: string, token?: string): Promise<void> {
    const response = await fetch(`${AI_SERVICE_URL}/documents/${documentId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }))
      throw new AIServiceError(
        errorData.detail || "Failed to delete document",
        response.status,
        errorData
      )
    }
  },

  /**
   * Health check for AI service
   */
  async healthCheck(): Promise<{ status: string; version: string }> {
    const response = await fetch(`${AI_SERVICE_URL}/health`, {
      method: "GET",
    })

    return handleResponse<{ status: string; version: string }>(response)
  },
}

export default aiClient

